import React from 'react';
import { VStack, HStack, Text, Icon, Box, Link } from '@chakra-ui/react';
import { FiCalendar, FiCopy, FiExternalLink } from 'react-icons/fi';
import { CgTimelapse } from 'react-icons/cg';
import { useTranslation } from '../../hooks/useTranslation';
import { SingleForwarding } from '../../api/mails/types';
import { InfoItem } from '../profile/InfoItem';
import Button from '../base/Button';
import { useOrderOneTimeForwardingMutation } from '../../api';
import { useAlert } from '../../hooks/useAlert';
import { modalManager } from '../../utils/modalManager';

interface SingleForwardingStatusProps {
    singleForwarding?: SingleForwarding;
}

const SingleForwardingStatus: React.FC<SingleForwardingStatusProps> = ({ singleForwarding }) => {
    const { t, locale } = useTranslation();
    const { success, error: showError } = useAlert();
    const [orderOneTimeForwarding, { isLoading: isConfirming }] = useOrderOneTimeForwardingMutation();

    const handleOrderOneTimeForwarding = () => {
        modalManager.show({
            title: t.pages.mail.orderForwardingTitle,
            message: t.pages.mail.orderForwardingMessage,
            buttons: [
                {
                    type: 'secondary',
                    text: t.pages.mail.cancelButton,
                    onClick: () => {
                        modalManager.remove();
                    },
                },
                {
                    type: 'success',
                    text: t.pages.mail.orderButton,
                    onClick: async () => {
                        try {
                            await orderOneTimeForwarding({ status: 'processing' }).unwrap();
                            success(t.pages.mail.orderForwardingSuccess);
                            modalManager.remove();
                        } catch (error) {
                            showError(t.pages.mail.orderForwardingError);
                        }
                    },
                },
            ],
            size: 'md',
        });
    };

    const handleCopyTrackNumber = async (trackNumber: string) => {
        try {
            await navigator.clipboard.writeText(trackNumber);
            success(t.pages.support.copied);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleConfirmReceipt = async (skipSuccess: boolean = false) => {
        try {
            await orderOneTimeForwarding({ status: 'completed' }).unwrap();
            if (!skipSuccess) {
                success(t.pages.mail.forwardingReceivedSuccess);
            }
        } catch (error) {
            showError(t.pages.mail.forwardingReceivedError);
        }
    };

    if (singleForwarding?.status === 'processing') {
        return (
            <VStack gap={3} align="stretch">
                <InfoItem
                    icon={FiCalendar}
                    label={t.pages.mail.singleForwardingRequestDate}
                    value={new Date(singleForwarding.request_date).toLocaleDateString(locale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                />
                <InfoItem
                    icon={CgTimelapse}
                    label={t.pages.mail.singleForwardingStatusLabel}
                    value={
                        t.pages.mail.singleForwardingStatus[
                            singleForwarding.status as keyof typeof t.pages.mail.singleForwardingStatus
                        ] || singleForwarding.status
                    }
                />
            </VStack>
        );
    }

    if (singleForwarding?.status === 'sent') {
        return (
            <>
                <Text fontSize="md" color="black" mb={2}>
                    {t.pages.mail.forwardingSentMessage}
                </Text>
                <Text fontSize="sm" color="gray.500" mb={4}>
                    {t.pages.mail.forwardingSentHint}
                </Text>
                {singleForwarding.track_number && (
                    <HStack justify="space-between" align="center" mb={4}>
                        <HStack gap={2} align="center" flex={1} minW={0}>
                            <Text fontSize="sm" color="gray.500">
                                {t.pages.mail.forwardingTrackNumber}
                            </Text>
                            <Text fontSize="sm" fontWeight="medium" color="black">
                                {singleForwarding.track_number}
                            </Text>
                        </HStack>
                        <Box
                            as="button"
                            onClick={() => handleCopyTrackNumber(singleForwarding.track_number!)}
                            p={1}
                            borderRadius="md"
                            _hover={{ bg: 'gray.100' }}
                            transition="background 0.2s"
                            cursor="pointer"
                        >
                            <Icon as={FiCopy} boxSize={4} color="gray.500" />
                        </Box>
                    </HStack>
                )}
                {singleForwarding.track_number && (
                    <Link
                        href={`https://track.bpost.cloud/btr/web/#/search?itemCode=${encodeURIComponent(
                            singleForwarding.track_number,
                        )}&lang=${locale}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        display="inline-flex"
                        alignItems="center"
                        gap={1}
                        fontSize="sm"
                        color="blue.500"
                        mb={4}
                    >
                        {t.pages.mail.forwardingTrackOnBpost}
                        <Icon as={FiExternalLink} boxSize={3.5} />
                    </Link>
                )}
                <Button
                    buttonType="success"
                    width="full"
                    onClick={() => handleConfirmReceipt()}
                    isLoading={isConfirming}
                >
                    {t.pages.mail.forwardingReceivedButton}
                </Button>
            </>
        );
    }

    if (singleForwarding?.status === 'empty') {
        return (
            <>
                <Text fontSize="md" color="black" mb={4}>
                    {t.pages.mail.forwardingEmptyMessage}
                </Text>
                <Button buttonType="success" width="full" onClick={() => handleConfirmReceipt(true)}>
                    {t.common.ok}
                </Button>
            </>
        );
    }

    return (
        <>
            <Text fontSize="md" color="black" mb={4}>
                {t.pages.mail.orderForwardingMessage}
            </Text>
            <Button buttonType="success" width="full" onClick={handleOrderOneTimeForwarding}>
                {t.pages.mail.orderButton}
            </Button>
        </>
    );
};

export default SingleForwardingStatus;
